import { useCallback, useState } from "react";
import {
  createDefaultDocumentAccessSettings,
  isDocumentAccessPasswordInvalid,
  type DocumentAccessSettingsState,
} from "./types";

/**
 * Tracks per-file password / client-visibility settings keyed by upload item id.
 * Prunes removed files and seeds defaults when a file completes.
 */
export function usePerFileDocumentAccessSettings() {
  const [settingsByFileId, setSettingsByFileId] = useState<
    Record<string, DocumentAccessSettingsState>
  >({});

  const resetFileAccessSettings = useCallback(() => {
    setSettingsByFileId({});
  }, []);

  const syncFromUploadFiles = useCallback((files: Array<{ id: string; status: string }>) => {
    setSettingsByFileId((prev) => {
      const next: Record<string, DocumentAccessSettingsState> = {};
      let changed = false;
      for (const file of files) {
        if (file.status !== "completed") continue;
        if (prev[file.id]) {
          next[file.id] = prev[file.id];
        } else {
          next[file.id] = createDefaultDocumentAccessSettings();
          changed = true;
        }
      }
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (!changed && prevKeys.length === nextKeys.length) {
        if (prevKeys.every((key) => key in next)) return prev;
      }
      return next;
    });
  }, []);

  const updateFileAccessSettings = useCallback(
    (fileId: string, next: DocumentAccessSettingsState) => {
      setSettingsByFileId((prev) => ({ ...prev, [fileId]: next }));
    },
    []
  );

  const hasInvalidPassword = Object.values(settingsByFileId).some(isDocumentAccessPasswordInvalid);

  return {
    settingsByFileId,
    syncFromUploadFiles,
    updateFileAccessSettings,
    resetFileAccessSettings,
    hasInvalidPassword,
  };
}
